import { TooltipProvider } from "@aircall/ds";
import { Tractor } from "@aircall/tractor";
import { PermissionsProvider } from "@dashboard/library";
import { PropsWithChildren } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { RequireAuth } from "@/components/RequireAuth/RequireAuth";
import { theme } from "@/config/theme";
import { useAuthenticationState } from "@/hooks/useAuthenticationState/useAuthenticationState";
import { ApolloClientProvider } from "@/providers/ApolloClientProvider/ApolloClientProvider";
import { AuthenticationProvider } from "@/providers/AuthenticationProvider/AuthenticationProvider";
import { DatadogRumInit } from "./components/DatadogRumInit/DatadogRumInit";
import { RestrictAccess } from "./components/RestrictAccess/RestrictAccess";
import { GlobalStyle } from "./GlobalStyle";
import { FeatureFlagsProvider } from "./providers/FeatureFlagProvider/FeatureFlagProvider";
import { I18nextProvider } from "./providers/I18nextProvider/I18nextProvider";
import { LicensesWrapper } from "./providers/LicensesWrapper/LicensesWrapper";
import { AppRoutes } from "./routes/AppRoutes";

function AuthenticatedApp({ children }: PropsWithChildren) {
  const {
    authState: { token },
  } = useAuthenticationState();

  if (!token) {
    throw new Error("Token is missing");
  }

  return (
    <DatadogRumInit>
      <ApolloClientProvider>
        <PermissionsProvider token={token}>
          <I18nextProvider>{children}</I18nextProvider>
        </PermissionsProvider>
      </ApolloClientProvider>
    </DatadogRumInit>
  );
}

export default function App() {
  return (
    <Tractor theme={theme}>
      <GlobalStyle />
      <TooltipProvider delay={250}>
        <Router>
          <ErrorBoundary>
            <AuthenticationProvider>
              <RequireAuth>
                <AuthenticatedApp>
                  <FeatureFlagsProvider>
                    <RestrictAccess>
                      <LicensesWrapper>
                        <AppRoutes />
                      </LicensesWrapper>
                    </RestrictAccess>
                  </FeatureFlagsProvider>
                </AuthenticatedApp>
              </RequireAuth>
            </AuthenticationProvider>
          </ErrorBoundary>
        </Router>
      </TooltipProvider>
    </Tractor>
  );
}
